import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Shield, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';

const PoliticaPrivacidad = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Política de Privacidad | Sinergia Empresarial CSCE</title>
        <meta name="description" content="Conoce cómo protegemos y tratamos tus datos personales de acuerdo con la legislación colombiana de protección de datos." />
        <meta name="robots" content="noindex, follow" />
        <link rel="canonical" href="https://www.sinergiaempresarialcsce.com/politica-privacidad" />
      </Helmet>
      {/* Header Section */}
      <section className="bg-gradient-to-r from-sinergia-azul to-sinergia-verde text-white py-16">
        <div className="container mx-auto px-6">
          <div className="flex items-center mb-4">
            <Shield className="w-12 h-12 mr-4" />
            <h1 className="heading-1 text-5xl font-bold">
              Política de Privacidad
            </h1>
          </div>
          <p className="body-large max-w-3xl">
            Última actualización: Diciembre 2025
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto px-6 max-w-4xl">
          <div className="bg-white rounded-lg shadow-md p-8 space-y-8">
            <div>
              <h2 className="heading-3 text-3xl font-bold mb-4 text-gray-900">
                Tratamiento de Datos Personales
              </h2>
              <p className="body-medium text-gray-700">
                En Sinergia Empresarial CSCE S.A.S., respetamos y protegemos la privacidad de nuestros clientes 
                y usuarios. Esta política describe cómo recopilamos, utilizamos y protegemos su información personal 
                de acuerdo con la legislación colombiana vigente sobre protección de datos.
              </p>
            </div>

            <div>
              <h3 className="heading-4 text-2xl font-semibold mb-3 text-gray-900">
                1. Responsable del Tratamiento
              </h3>
              <p className="body-medium text-gray-700 mb-2">
                <strong>Razón Social:</strong> Sinergia Empresarial CSCE S.A.S.
              </p>
              <p className="body-medium text-gray-700 mb-2">
                <strong>Domicilio:</strong> Colombia
              </p>
              <p className="body-medium text-gray-700">
                <strong>Email de contacto:</strong> contacto@sinergiaempresarialcsce.com
              </p>
            </div>

            <div>
              <h3 className="heading-4 text-2xl font-semibold mb-3 text-gray-900">
                2. Datos que Recopilamos
              </h3>
              <p className="body-medium text-gray-700 mb-3">
                Podemos recopilar los siguientes tipos de información personal:
              </p>
              <ul className="list-disc list-inside space-y-2 body-medium text-gray-700 ml-4">
                <li>Nombre completo</li>
                <li>Nombre de la empresa</li>
                <li>Email corporativo o personal</li>
                <li>Número de teléfono</li>
                <li>Ciudad de residencia o ubicación de empresa</li>
                <li>Información sobre su empresa (tamaño, necesidades, etc.)</li>
              </ul>
            </div>

            <div>
              <h3 className="heading-4 text-2xl font-semibold mb-3 text-gray-900">
                3. Finalidad del Tratamiento
              </h3>
              <p className="body-medium text-gray-700 mb-3">
                Sus datos personales serán utilizados para:
              </p>
              <ul className="list-disc list-inside space-y-2 body-medium text-gray-700 ml-4">
                <li>Responder a sus consultas y solicitudes de información</li>
                <li>Agendar y realizar consultas iniciales</li>
                <li>Elaborar propuestas comerciales personalizadas</li>
                <li>Prestar los servicios contratados</li>
                <li>Enviar información relevante sobre nuestros servicios</li>
                <li>Cumplir con obligaciones legales y contractuales</li>
              </ul>
            </div>

            <div>
              <h3 className="heading-4 text-2xl font-semibold mb-3 text-gray-900">
                4. Derechos del Titular
              </h3>
              <p className="body-medium text-gray-700 mb-3">
                Como titular de datos personales, usted tiene derecho a:
              </p>
              <ul className="list-disc list-inside space-y-2 body-medium text-gray-700 ml-4">
                <li>Conocer, actualizar y rectificar sus datos personales</li>
                <li>Solicitar prueba de la autorización otorgada</li>
                <li>Ser informado sobre el uso dado a sus datos</li>
                <li>Presentar quejas ante la Superintendencia de Industria y Comercio</li>
                <li>Revocar la autorización y/o solicitar la supresión de datos cuando sea procedente</li>
                <li>Acceder de forma gratuita a sus datos personales</li>
              </ul>
            </div>

            <div>
              <h3 className="heading-4 text-2xl font-semibold mb-3 text-gray-900">
                5. Seguridad de la Información
              </h3>
              <p className="body-medium text-gray-700">
                Implementamos medidas de seguridad técnicas, administrativas y físicas apropiadas para 
                proteger su información personal contra acceso no autorizado, pérdida, destrucción o 
                alteración. Sin embargo, ninguna transmisión por internet es completamente segura.
              </p>
            </div>

            <div>
              <h3 className="heading-4 text-2xl font-semibold mb-3 text-gray-900">
                6. Compartir Información
              </h3>
              <p className="body-medium text-gray-700">
                No compartimos, vendemos ni alquilamos su información personal a terceros, excepto cuando 
                sea necesario para prestar nuestros servicios o cuando la ley lo requiera. Toda información 
                compartida con proveedores de servicios estará sujeta a acuerdos de confidencialidad.
              </p>
            </div>

            <div>
              <h3 className="heading-4 text-2xl font-semibold mb-3 text-gray-900">
                7. Conservación de Datos
              </h3>
              <p className="body-medium text-gray-700">
                Conservaremos sus datos personales durante el tiempo necesario para cumplir con las 
                finalidades descritas y para cumplir con obligaciones legales, resolver disputas y 
                hacer cumplir nuestros acuerdos.
              </p>
            </div>

            <div>
              <h3 className="heading-4 text-2xl font-semibold mb-3 text-gray-900">
                8. Ejercicio de Derechos
              </h3>
              <p className="body-medium text-gray-700">
                Para ejercer sus derechos o realizar consultas sobre el tratamiento de sus datos 
                personales, puede contactarnos a través de:
              </p>
              <p className="body-medium text-gray-700 mt-3">
                <strong>Email:</strong> contacto@sinergiaempresarialcsce.com
              </p>
              <p className="body-medium text-gray-700 mt-2">
                Responderemos a su solicitud en un plazo máximo de 15 días hábiles.
              </p>
            </div>

            <div>
              <h3 className="heading-4 text-2xl font-semibold mb-3 text-gray-900">
                9. Cambios a esta Política
              </h3>
              <p className="body-medium text-gray-700">
                Nos reservamos el derecho de actualizar esta política de privacidad periódicamente. 
                Los cambios serán publicados en esta página con la fecha de "Última actualización" 
                correspondiente. Le recomendamos revisar esta política regularmente.
              </p>
            </div>

            <div>
              <h3 className="heading-4 text-2xl font-semibold mb-3 text-gray-900">
                10. Autorización
              </h3>
              <p className="body-medium text-gray-700">
                Al proporcionar sus datos personales a través de nuestro sitio web o cualquier otro 
                medio, usted autoriza a Sinergia Empresarial CSCE S.A.S. para el tratamiento de los 
                mismos conforme a esta política de privacidad y para las finalidades aquí descritas.
              </p>
            </div>

            <div className="bg-gray-50 border border-sinergia-gris-claro rounded-lg p-6 mt-8">
              <h3 className="heading-5 text-xl font-semibold mb-3 text-sinergia-gris-oscuro">
                Consentimiento
              </h3>
              <p className="body-medium text-gray-700">
                Al utilizar nuestros servicios y proporcionar su información, usted declara haber 
                leído, entendido y aceptado los términos de esta Política de Privacidad.
              </p>
            </div>
          </div>

          <div className="mt-8 text-center">
            <Link to="/contacto">
              <Button className="btn-primary px-8 py-3">
                <ArrowLeft className="mr-2 w-5 h-5" />
                Volver al formulario
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PoliticaPrivacidad;
